
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { format } from 'date-fns';
import { Building, ExternalLink, Phone, MapPin, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { LeadStatusSelect } from './LeadStatusSelect';
import { LeadsTableProps } from './types';
import { useNavigate } from 'react-router-dom';

export function LeadsTableMobile({ leads, onLeadUpdate, filters }: LeadsTableProps) {
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);

  const handleDeleteLead = async (id: string) => {
    try {
      setIsDeleting(id);
      const { error } = await supabase
        .from('nursing_home_leads')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
      
      toast.success('Lead removed successfully');
      // Call onLeadUpdate to refresh the list after deletion
      onLeadUpdate();
    } catch (error) {
      console.error('Error deleting lead:', error);
      toast.error('Failed to remove lead');
    } finally {
      setIsDeleting(null);
    }
  };

  const handleStatusChange = async (id: string, newStatus: string) => {
    try {
      setUpdatingStatus(id);
      const { error } = await supabase
        .from('nursing_home_leads')
        .update({ status: newStatus })
        .eq('id', id);
        
      if (error) throw error;
      
      toast.success(`Status updated to ${newStatus}`);
      onLeadUpdate();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleViewNursingHome = (countySlug: string | undefined, nursingHomeId: string) => {
    if (countySlug) {
      window.open(`/nursing-homes/${countySlug.toLowerCase()}/${nursingHomeId}`, '_blank');
    } else {
      window.open(`/nursing-homes/${nursingHomeId}`, '_blank');
    }
  };

  const filteredLeads = leads.filter(lead => {
    if (filters.status && lead.status !== filters.status) return false;
    if (filters.county && lead.location !== filters.county) return false;
    return true;
  });

  if (filteredLeads.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground bg-gray-50 rounded-lg">
        No nursing home leads found with the current filters
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {filteredLeads.map((lead) => (
        <Card key={lead.id} className="p-4 overflow-hidden">
          {/* Card Header */}
          <div className="flex items-start justify-between gap-2 mb-3">
            <div className="flex items-start gap-2 cursor-pointer" onClick={() => navigate(`/dashboard/leads/${lead.id}`)}>
              <div className="w-10 h-10 bg-grandpal-beige rounded-full flex items-center justify-center shrink-0">
                <Building className="h-5 w-5 text-grandpal-green" />
              </div>
              <div className="max-w-[180px]">
                <h3 className="font-medium text-base truncate" title={lead.nursing_home_name}>{lead.nursing_home_name}</h3>
                <div className="flex flex-wrap items-center text-xs gap-1">
                  <span className={lead.category === 'Private' ? 'text-purple-600' : 'text-blue-600'}>
                    {lead.category || 'Unknown'}
                  </span>
                  {lead.location && (
                    <>
                      <span className="mx-1">•</span>
                      <span className="text-gray-500 capitalize">{lead.location}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
            
            <LeadStatusSelect
              leadId={lead.id}
              status={lead.status || "interested"}
              updatingStatus={updatingStatus}
              onStatusChange={handleStatusChange}
              variant="compact"
            />
          </div>

          {/* Lead Info Section */}
          <div className="mb-3 px-1">
            {lead.location && (
              <div className="flex items-center text-sm text-gray-600 mb-1">
                <MapPin className="h-3.5 w-3.5 mr-1.5" />
                <span className="truncate">{lead.location}</span>
              </div>
            )}
            
            <div className="flex flex-wrap gap-2 my-2 text-xs">
              <Badge variant="outline" className="font-normal text-gray-500">
                Added {format(new Date(lead.created_at), 'MMM d, yyyy')}
              </Badge>
            </div>

            {lead.notes && (
              <div className="text-sm text-gray-700 mt-2 pb-2 border-t border-gray-100 pt-2 max-h-16 overflow-y-auto">
                <p className="line-clamp-2">{lead.notes}</p>
              </div>
            )}
          </div>
          
          {/* Action Buttons */}
          <div className="flex justify-between border-t pt-3 mt-2">
            <div className="flex space-x-2">
              <Button 
                size="sm" 
                variant="outline" 
                className="h-9 flex gap-1.5 items-center text-xs"
                onClick={() => handleViewNursingHome(lead.location?.toLowerCase(), lead.nursing_home_id)}
              >
                <ExternalLink className="h-3.5 w-3.5" />
                <span>View Profile</span>
              </Button>
            </div>
            
            <Button 
              size="sm" 
              variant="ghost" 
              className="h-9 w-9 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 flex items-center justify-center"
              onClick={() => handleDeleteLead(lead.id)}
              disabled={isDeleting === lead.id}
            >
              {isDeleting === lead.id ? (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-red-500 border-t-transparent" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
            </Button>
          </div>
        </Card>
      ))}
    </div>
  );
}
